import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { api } from "./api";
import { useMe } from "./queries";

// Replaces the Clerk-ish surface area we used. The session lives in an
// HttpOnly cookie set by /api/auth/google; useMe pings /api/me to derive
// signed-in state.
//
//   me      — the current user or null (null = signed-out)
//   status  — "loading" | "signed-in" | "signed-out"
//   signOut — POST /api/auth/logout, clear the query cache, redirect on
//             the caller's side if needed
export function useAuth() {
  const qc = useQueryClient();
  const { data: me, isLoading, isFetching, isFetched } = useMe();
  // Race-safe status. isLoading is only true on the very first fetch (no
  // cached data yet). After that, a refetch sets isFetching=true while
  // surfacing the previously-cached data. If that data is null and a
  // refetch is in flight, we treat it as "loading" so SignedInOnly
  // doesn't bounce a freshly-signed-in user back to /sign-in while /me
  // is being repopulated.
  const status: "loading" | "signed-in" | "signed-out" = me
    ? "signed-in"
    : isLoading || (isFetching && !isFetched)
      ? "loading"
      : isFetching && !me
        ? "loading"
        : "signed-out";

  const signOut = useCallback(async () => {
    await api("/auth/logout", { method: "POST", public: true });
    qc.setQueryData(["me"], null);
    qc.invalidateQueries();
  }, [qc]);

  return { me: me ?? null, status, signOut };
}
